import React, { useEffect } from "react";
import {
  useHistory,
  useParams,
} from "react-router-dom";
import {
  useDispatch,
  useSelector,
} from "react-redux";

import PageLayout from "../../../components/PageLayout/PageLayout";

import { AppDispatch } from "../../../app/store";

import {
  getAdminUserById,
  selectAdminSelectedUser,
  selectAdminUsersLoading,
  selectAdminUsersError,
  clearSelectedUser,
} from "../../../slices/adminUser/adminUserSlice";

import styles from "./UserDetails.module.css";

interface RouteParams {
  id: string;
}

const UserDetails: React.FC = () => {
  const { id } = useParams<RouteParams>();
  const history = useHistory();

  const dispatch = useDispatch<AppDispatch>();

  const user = useSelector(selectAdminSelectedUser);
  const loading = useSelector(selectAdminUsersLoading);
  const error = useSelector(selectAdminUsersError);

  useEffect(() => {
    dispatch(getAdminUserById(id));

    return () => {
      dispatch(clearSelectedUser());
    };
  }, [dispatch, id]);

  const formatDate = (date?: string | null) => {
    if (!date) return "Nigdy";

    return new Date(date).toLocaleDateString("pl-PL");
  };

  const formatDateTime = (date?: string | null) => {
    if (!date) return "Nigdy";

    return new Date(date).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getBillStatus = (status: string) => {
    switch (status) {
      case "pending":
        return "Do zapłaty";

      case "paid":
        return "Opłacony";

      case "cancelled":
        return "Anulowany";

      default:
        return status;
    }
  };

  return (
    <PageLayout className={styles.UserDetails}>
      <div className={styles.Top}>
        <h1 className={styles.Title}>
          Szczegóły użytkownika
        </h1>

        <button
          type="button"
          className={styles.BackButton}
          onClick={() => history.push("/admin/users")}
        >
          ← Wróć do użytkowników
        </button>
      </div>

      {loading && (
        <div className={styles.Message}>
          Pobieranie użytkownika...
        </div>
      )}

      {!loading && error && (
        <div className={styles.Error}>
          {error}
        </div>
      )}

      {!loading && !error && user && (
        <>
          <div className={styles.InfoBox}>
            <div className={styles.InfoTitle}>
              Dane użytkownika
            </div>

            <div className={styles.InfoText}>
              Informacje o koncie, aktywności oraz
              należnościach użytkownika.
            </div>
          </div>

          <div className={styles.UserCard}>
            <div className={styles.UserMain}>
              <div className={styles.Name}>
                {user.name}
              </div>

              <div className={styles.Email}>
                {user.email}
              </div>
            </div>

            <div className={styles.Info}>
              <span className={styles.Label}>
                Do zapłaty:
              </span>

              <strong>
                {Number(
                  user.outstandingBalance || 0
                ).toFixed(2)}{" "}
                PLN
              </strong>
            </div>

            <div className={styles.Info}>
              <span className={styles.Label}>
                Ostatnie logowanie:
              </span>

              <span>
                {formatDateTime(user.lastLoginAt)}
              </span>
            </div>

            <div className={styles.Info}>
              <span className={styles.Label}>
                Rejestracja:
              </span>

              <span>
                {formatDate(user.createdAt)}
              </span>
            </div>

            <div className={styles.Info}>
              <span className={styles.Label}>
                Status konta:
              </span>

              <span>
                {user.verified
                  ? "Zweryfikowane"
                  : "Niezweryfikowane"}
              </span>
            </div>
          </div>

          <h2 className={styles.SectionTitle}>
            Rachunki
          </h2>

          {!user.bills || user.bills.length === 0 ? (
            <div className={styles.Empty}>
              Brak rachunków.
            </div>
          ) : (
            <div className={styles.Bills}>
              <div className={styles.BillHeader}>
                <div>Rachunek:</div>
                <div>Kwota:</div>
                <div>Status:</div>
                <div>Data:</div>
              </div>

              {user.bills.map((bill) => (
                <div
                  key={bill._id}
                  className={styles.BillRow}
                >
                  <div className={styles.BillTitle}>
                    <span className={styles.MobileLabel}>
                      Rachunek
                    </span>

                    {bill.title || bill.type}
                  </div>

                  <div className={styles.BillAmount}>
                    <span className={styles.MobileLabel}>
                      Kwota
                    </span>

                    {Number(bill.amount).toFixed(2)} PLN
                  </div>

                  <div className={styles.BillStatus}>
                    <span className={styles.MobileLabel}>
                      Status
                    </span>

                    {getBillStatus(bill.status)}
                  </div>

                  <div className={styles.BillDate}>
                    <span className={styles.MobileLabel}>
                      Data
                    </span>

                    {formatDate(bill.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default UserDetails;